// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import Guard from "../common/guard";
import { IProject } from "../models/applicationState";
import { IStorageProvider, StorageProviderFactory } from "../providers/storage/storageProviderFactory";
import { constants } from "../common/constants";
import ServiceHelper from "./serviceHelper";
import { strings } from "../common/strings";
import { RinktMLClient } from "./rinktService";
import {IImpersonation} from "../models/applicationState";

export enum OcrStatus {
    loadingFromAzureBlob = "loadingFromAzureBlob",
    runningOCR = "runningOCR",
    done = "done",
    failed = "failed",
}

/**
 * @name - OCR Service
 * @description - Functions for dealing with OCR
 */
export class OCRService {
    private storageProviderInstance: IStorageProvider;

    constructor(private project: IProject, private authToken: string, private impersonation: IImpersonation) {
        Guard.null(project);
        Guard.null(authToken);
        Guard.null(impersonation);
    }

    /**
     * get recognized text from OCR service
     * @param filePath - filepath sent to OCR
     * @param fileName - name of OCR file
     */
    public async getRecognizedText(
        filePath: string,
        fileName: string,
        mimeType: string,
        onStatusChanged?: (ocrStatus: OcrStatus) => void,
        rewrite?: boolean
    ): Promise<any> {
        Guard.empty(filePath);

        const notifyStatusChanged = (ocrStatus: OcrStatus) => onStatusChanged && onStatusChanged(ocrStatus);
        const ocrFileName = decodeURIComponent(`${fileName}${constants.ocrFileExtension}`);

        let ocrJson;
        try {
            notifyStatusChanged(OcrStatus.loadingFromAzureBlob);
            ocrJson = await this.readOcrFile(ocrFileName);
            if (!this.isValidOcrFormat(ocrJson) || rewrite) {
                ocrJson = await this.fetchOcrUriResult(filePath, fileName, ocrFileName, mimeType);
            }
        } catch (e) {
            notifyStatusChanged(OcrStatus.runningOCR);
            ocrJson = await this.fetchOcrUriResult(filePath, fileName, ocrFileName, mimeType);
        } finally {
            if (ocrJson) {
                notifyStatusChanged(OcrStatus.done);
            }
            else {
                notifyStatusChanged(OcrStatus.failed);
            }
        }
        return ocrJson;
    }

    /**
     * Get Storage Provider from project's target connection
     */
    protected get storageProvider(): IStorageProvider {
        if (!this.storageProviderInstance) {
            this.storageProviderInstance = StorageProviderFactory.create(
                "azureBlobStorage",{
                    "auth": this.authToken,
                    "impersonation": this.impersonation,
                }
            );
        }
        return this.storageProviderInstance;
    }

    private readOcrFile = async (ocrFileName: string) => {
        const filePath = this.project.folderPath + ocrFileName
        const json = await this.storageProvider.readText(filePath, true);
        if (json !== null) {
            return new Promise((resolve, reject) => {
                resolve(JSON.parse(json));
            });
        }
    }

    private fetchOcrUriResult = async (filePath: string, fileName: string, ocrFileName: string, mimeType: string) => {
        try {
            let headers;
            if (filePath.startsWith("file:")) {
                headers = { "Content-Type": mimeType, "cache-control": "no-cache" };
            } else {
                headers = { "Content-Type": "application/json" };
            }
            headers["Authorization"] = this.authToken;
            if (this.impersonation && this.impersonation.impersonatedUser) {
                headers['Impersonation'] = this.impersonation.impersonatedUser.uid
            }

            const rinktClient = new RinktMLClient(this.authToken, this.impersonation)
            const response = await rinktClient.analyze(filePath)

            const operationLocation = response.headers["operation-location"];
            const apiKey = response.headers["ocp-apim-subscription-key"];
            return this.poll(
                () => ServiceHelper.getWithAutoRetry(operationLocation, { headers }, apiKey as string),
                120000,
                1500).then(async (data) => {
                    await this.save(ocrFileName, data);
                    return data;
                });
        } catch (error) {
            if (error?.toJSON()?.message === "Network Error" || error.response.status === 400) {
                throw new Error(strings.errors.getOcrError.message);
            } else {
                throw new Error(error);
            }
        }
    }

    /**
     * Save OCR
     * @param metadata - Metadata for asset
     */
    private async save(fileName: string, ocrJson: any): Promise<any> {
        Guard.empty(fileName);
        Guard.null(ocrJson);

        await this.storageProvider.writeText(fileName, JSON.stringify(ocrJson, null, 4), this.project.id);
        return ocrJson;
    }

    /**
     * Poll function to repeatly check if request succeeded
     * @param func - function that will be called repeatly
     * @param timeout - timeout
     * @param interval - interval
     */
    private poll = (func, timeout, interval): Promise<any> => {
        const endTime = Number(new Date()) + (timeout || 10000);
        interval = interval || 100;

        const checkSucceeded = (resolve, reject) => {
            const ajax = func();
            ajax.then((response) => {
                if (response.data.status.toLowerCase() === constants.statusCodeSucceeded) {
                    resolve(response.data);
                } else if (Number(new Date()) < endTime) {
                    // If the request isn't succeeded and the timeout hasn't elapsed, go again
                    setTimeout(checkSucceeded, interval, resolve, reject);
                } else {
                    // Didn't succeeded after too much time, reject
                    reject(new Error("Timed out for getting Layout results"));
                }
            });
        };

        return new Promise(checkSucceeded);
    }

    private isValidOcrFormat = (ocr): boolean => {
        return ocr && ocr.analyzeResult && ocr.analyzeResult.readResults;
    }
}
